import { useState } from "react";
import { Modal, Btn, Input } from "@/components/Shared";

// RateChangeDialog — shown when an admin changes a scalar rate on the
// Edit form (Merchant.merchant_rate or Gateway.lob_rates[].base_rate).
// Asks for `effective_from` (date) so the change is captured as a
// rate-version row. Also collects an optional reason.
//
// Props:
//   open / onClose       visibility control
//   onConfirm({effective_from, reason})  called with picked date+reason
//   oldRate / newRate    decimal rates for the side-by-side display.
//                        Used when there's exactly ONE change (single-LOB
//                        gateway edit, or merchant MDR edit).
//   entityLabel          e.g. "Merchant M001" or "Gateway Cashfree · UPI"
//   changes              optional list of changes for multi-LOB gateway
//                        edits — when present, the dialog renders a row
//                        per (lob, oldRate, newRate) instead of the
//                        single side-by-side card. ONE effective_from is
//                        applied to ALL rows on confirm.
export default function RateChangeDialog({ open, onClose, onConfirm, oldRate, newRate, entityLabel, changes }) {
  // Default to local-tz today so users in IST/etc. see the date they
  // expect (matches todayYmd() in lib/api.js — see iter28 timezone fix).
  const localToday = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const [effectiveFrom, setEffectiveFrom] = useState(localToday);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;
  const fmt = (v) => `${(Number(v || 0) * 100).toFixed(4).replace(/\.?0+$/, "")}%`;
  const isMulti = Array.isArray(changes) && changes.length > 1;

  const submit = async () => {
    if (!effectiveFrom) return;
    setBusy(true);
    try { await onConfirm({ effective_from: effectiveFrom, reason: reason.trim() }); }
    finally {
      setBusy(false);
      setEffectiveFrom(localToday);
      setReason("");
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Confirm rate change" testid="rate-change-dialog" size="md">
      <div className="space-y-4">
        <div className="text-[12.5px] text-[var(--muted)] leading-relaxed">
          You're changing {isMulti ? `${changes.length} rates on ` : "the rate on "}
          <b className="text-[var(--ink)]">{entityLabel}</b>. We'll record {isMulti ? "each" : "this"} as a new
          version in the rate history — the previous values stay queryable for any historical period.
        </div>

        {isMulti ? (
          <div className="border border-[var(--line)] rounded-sm overflow-hidden" data-testid="rcd-changes-list">
            <table className="table-data w-full">
              <thead>
                <tr><th>LOB</th><th className="text-right">Previous</th><th className="text-right">New</th></tr>
              </thead>
              <tbody>
                {changes.map((c) => (
                  <tr key={c.lob} data-testid={`rcd-change-${c.lob}`}>
                    <td className="text-[12px] font-medium">{c.lob}</td>
                    <td className="num text-right text-[12px] text-[var(--muted)]">{fmt(c.oldRate)}</td>
                    <td className="num text-right text-[12px] font-semibold text-[var(--accent)]">{fmt(c.newRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-[var(--surface-2)] border border-[var(--line)] rounded-sm p-3 grid grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">Previous</div>
              <div className="num text-lg font-semibold mt-0.5">{fmt(oldRate)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">New</div>
              <div className="num text-lg font-semibold mt-0.5 text-[var(--accent)]">{fmt(newRate)}</div>
            </div>
          </div>
        )}

        <div>
          <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Effective from <span className="text-[var(--negative)]">*</span></label>
          <Input
            type="date"
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            data-testid="rcd-effective-from"
          />
          <div className="text-[10.5px] text-[var(--muted)] mt-1">
            Today by default. Pick a future date to schedule the change — current display rate{isMulti ? "s" : ""} stay{isMulti ? "" : "s"} unchanged until then.
          </div>
        </div>
        <div>
          <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Reason (optional)</label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Renegotiated MDR after volume review"
            data-testid="rcd-reason"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Btn variant="ghost" onClick={onClose} testid="rcd-cancel">Cancel</Btn>
          <Btn variant="accent" onClick={submit} disabled={busy || !effectiveFrom} testid="rcd-confirm">
            {busy ? "Saving…" : (isMulti ? `Save & version ${changes.length} rates` : "Save & version")}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
